import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
    fetchAllUsers, 
    updateUser, 
    deactivateUser, 
    resetUserCode
} from '../../store/slices/adminReducer';
import AdminSidebar from '../../components/Admin/AdminSidebar';
import AdminHeader from '../../components/Admin/AdminHeader';
import DataTable from '../../components/Admin/DataTable';
import StatsCard from '../../components/Admin/StatsCard';
import { useAppAlertDialog } from '../../hooks/useAppAlertDialog';
import { useTranslation } from '../../hooks/useTranslation';

function UserManagement() {
    const { t } = useTranslation();
    const { auth } = useSelector((state) => state.affiliate);
    const { users, system } = useSelector((state) => state.admin);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showUserModal, setShowUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const { showAlert, showConfirm, dialogNode } = useAppAlertDialog({
        cancelLabel: t("Avbryt"),
        confirmLabel: t("Fortsätt"),
        closeLabel: t("Stäng"),
    });

    // Check if user is admin
    useEffect(() => {
        if (auth?.affiliate_role !== "admin") {
            navigate("/login");
        }
    }, [auth, navigate]);

    // Fetch users on component mount
    useEffect(() => {
        if (auth?.affiliate_role === "admin") {
            dispatch(fetchAllUsers());
        }
    }, [dispatch, auth]);

    const handleSearch = (term) => {
        dispatch(fetchAllUsers({ search: term }));
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setShowUserModal(true);
    };

    const handleUpdateUser = async (userData) => {
        try {
            await dispatch(updateUser({ userId: editingUser.id, userData }));
            setShowUserModal(false);
            setEditingUser(null);
            dispatch(fetchAllUsers()); // Refresh the list
        } catch (error) {
            console.error('Failed to update user:', error);
        }
    };

    const handleDeactivateUser = async (userId) => {
        const confirmed = await showConfirm(
            t('Deactivate user'),
            t('Are you sure you want to deactivate this user?')
        );
        if (!confirmed) return;
        try {
            await dispatch(deactivateUser(userId));
            dispatch(fetchAllUsers()); // Refresh the list
        } catch (error) {
            console.error('Failed to deactivate user:', error);
        }
    };

    const handleResetCode = async (userId) => {
        const confirmed = await showConfirm(
            t('Reset code'),
            t('Are you sure you want to reset this user\'s identification code?')
        );
        if (!confirmed) return;
        try {
            await dispatch(resetUserCode(userId));
            dispatch(fetchAllUsers()); // Refresh the list
        } catch (error) {
            console.error('Failed to reset user code:', error);
        }
    };

    const handleBulkAction = async (action) => {
        if (selectedUsers.length === 0) {
            showAlert(t('No users selected'), t('Please select users first'));
            return;
        }

        const confirmed = await showConfirm(
            t('Confirm bulk action'),
            t('Are you sure you want to {{action}} {{count}} users?', { action, count: selectedUsers.length })
        );
        if (!confirmed) return;
        try {
            // Implement bulk actions here
            console.log(`Bulk ${action} for users:`, selectedUsers);
            setSelectedUsers([]);
            dispatch(fetchAllUsers()); // Refresh the list
        } catch (error) {
            console.error(`Failed to ${action} users:`, error);
        }
    };

    const columns = [
        {
            header: t('User'),
            accessor: 'name',
            render: (value, row) => (
                <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                            {row.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900">{row.name}</p>
                        <p className="text-sm text-gray-500">{row.email}</p>
                    </div>
                </div>
            )
        },
        {
            header: t('Role'),
            accessor: 'affiliate_role',
            render: (value) => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    value === 'admin' 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                }`}>
                    {value}
                </span>
            )
        },
        {
            header: t('Phone'),
            accessor: 'phone',
            render: (value) => value || t('Not provided')
        },
        {
            header: t('Created'),
            accessor: 'created_at',
            render: (value) => new Date(value).toLocaleDateString()
        },
        {
            header: t('Status'),
            accessor: 'deleted_at',
            render: (value) => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    value ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                }`}>
                    {value ? t('Inactive') : t('Active')}
                </span>
            )
        },
        {
            header: t('Actions'),
            accessor: 'id',
            render: (value, row) => (
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => handleEditUser(row)}
                        className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
                        title={t("Edit user")}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                    <button
                        onClick={() => handleResetCode(value)}
                        className="p-1 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 rounded"
                        title={t("Reset code")}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                    <button
                        onClick={() => handleDeactivateUser(value)}
                        className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                        title={t("Deactivate user")}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                </div>
            )
        }
    ];

    const bulkActions = [
        {
            label: t('Create User'),
            icon: '➕',
            variant: 'primary',
            onClick: () => navigate('/admin/users/create')
        },
        {
            label: t('Activate'),
            icon: '✅',
            variant: 'secondary',
            onClick: () => handleBulkAction('activate')
        },
        {
            label: t('Deactivate'),
            icon: '❌',
            variant: 'danger',
            onClick: () => handleBulkAction('deactivate')
        },
        {
            label: t('Export'),
            icon: '📤',
            variant: 'secondary',
            onClick: () => handleBulkAction('export')
        }
    ];

    if (auth?.affiliate_role !== "admin") {
        return null;
    }

    return (
        <div className="min-h-screen bg-gray-100 flex">
            {/* Sidebar */}
            <AdminSidebar 
                collapsed={sidebarCollapsed} 
                onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
            />

            {/* Main Content */}
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <AdminHeader 
                    onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
                    sidebarCollapsed={sidebarCollapsed}
                />

                {/* Content */}
                <div className="flex-1 p-6">
                    {/* Page Header */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
                                <p className="text-gray-600">
                                    {t("Manage users, roles, and permissions for your organization.")}
                                </p>
                            </div>
                            <Link 
                                to="/admin/users/create"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                + {t("Create User")}
                            </Link>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-4 mobile:grid-cols-1  gap-6 mb-8">
                        <StatsCard
                            title={t("Total Users")}
                            value={system?.stats?.users?.total || 0}
                            icon="👥"
                            color="blue"
                            loading={system.loading}
                        />
                        <StatsCard
                            title={t("Active Users")}
                            value={system?.stats?.users?.active || 0}
                            icon="✅"
                            color="green"
                            loading={system.loading}
                        />
                        <StatsCard
                            title={t("Admin Users")}
                            value={system?.stats?.users?.admins || 0}
                            icon="👑"
                            color="purple"
                            loading={system.loading}
                        />
                        <StatsCard
                            title={t("Affiliate Users")}
                            value={system?.stats?.users?.affiliates || 0}
                            icon="👤"
                            color="indigo"
                            loading={system.loading}
                        />
                    </div>

                    {/* Users Table */}
                    <DataTable
                        data={users.list}
                        columns={columns}
                        loading={users.loading}
                        pagination={users.pagination}
                        onPageChange={(page) => dispatch(fetchAllUsers({ page }))}
                        onSearch={handleSearch}
                        searchable={true}
                        actions={bulkActions}
                        emptyMessage={t("No users found")}
                    />

                    {/* User Edit Modal */}
                    {showUserModal && editingUser && (
                        <UserEditModal
                            user={editingUser}
                            t={t}
                            onClose={() => {
                                setShowUserModal(false);
                                setEditingUser(null);
                            }}
                            onSave={handleUpdateUser}
                        />
                    )}
                    {dialogNode}
                </div>
            </div>
        </div>
    );
}

// User Edit Modal Component
const UserEditModal = ({ user, onClose, onSave, t }) => {
    const [formData, setFormData] = useState({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        affiliate_role: user.affiliate_role || 'affiliate'
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("Edit User")}</h3>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("Name")}
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("Email")}
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("Phone")}
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("Role")}
                            </label>
                            <select
                                name="affiliate_role"
                                value={formData.affiliate_role}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="affiliate">{t("Affiliate")}</option>
                                <option value="admin">{t("Admin")}</option>
                            </select>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                            >
                                {t("Cancel")}
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                            >
                                {t("Save Changes")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UserManagement;
